import { useMemo, useState, type RefObject } from 'react';
import ClusterCardView from './ClusterCardView';
import ClusterFlow from './ClusterFlow';
import ClusterTable from './ClusterTable';
import { reclusterByMode } from '../utils/recluster';
import '../styles/cluster-tree.css';
import type { ClusterData, ClusterModeId, HierarchicalNode } from '../types';

type Props = {
  mode?: 'flow' | 'paths' | 'card';
  data: ClusterData;
  fullscreenRootRef?: RefObject<HTMLDivElement | null>;
  onHierarchicalChange?: (next: HierarchicalNode) => void;
};

export default function ClusterTreeShell({
  mode = 'flow',
  data,
  fullscreenRootRef,
  onHierarchicalChange
}: Props) {
  const [clusterMode, setClusterMode] = useState<ClusterModeId | string>(
    'default'
  );
  const view = useMemo(
    () => reclusterByMode(data.hierarchical, clusterMode),
    [data.hierarchical, clusterMode]
  );
  const viewClusterData = useMemo<ClusterData>(
    () => ({
      ...data,
      hierarchical: view.hierarchical,
      table: view.table,
      cluster_mode: clusterMode,
      cluster_summary: view.summary,
      cluster_thresholds: view.thresholds ?? null
    }),
    [clusterMode, data, view]
  );

  const canEditNodes =
    clusterMode === 'default' && Boolean(onHierarchicalChange);

  const handleHierarchicalChange = (next: HierarchicalNode) => {
    // Edits always apply to the source tree (default mode only).
    onHierarchicalChange?.(next);
  };

  if (mode === 'paths') {
    return (
      <div dir="rtl">
        <ClusterTable
          rows={viewClusterData.table ?? []}
          clusterData={viewClusterData}
          clusterMode={clusterMode}
          onClusterModeChange={setClusterMode}
          clusterSummary={view.summary}
          thresholds={view.thresholds}
        />
      </div>
    );
  }

  if (mode === 'card') {
    return (
      <ClusterCardView
        hierarchical={view.hierarchical}
        rows={viewClusterData.table ?? []}
        clusterData={viewClusterData}
        clusterMode={clusterMode}
        onClusterModeChange={setClusterMode}
        clusterSummary={view.summary}
        thresholds={view.thresholds}
      />
    );
  }

  return (
    <div className="cluster-tree-shell d-flex flex-column" dir="rtl">
      <ClusterFlow
        hierarchical={
          clusterMode === 'default' ? data.hierarchical : view.hierarchical
        }
        clusterData={
          clusterMode === 'default'
            ? { ...data, hierarchical: data.hierarchical }
            : viewClusterData
        }
        clusterMode={clusterMode}
        onClusterModeChange={setClusterMode}
        clusterSummary={view.summary}
        thresholds={view.thresholds}
        canEditNodes={canEditNodes}
        fullscreenRootRef={fullscreenRootRef}
        onHierarchicalChange={
          canEditNodes ? handleHierarchicalChange : undefined
        }
      />
    </div>
  );
}
